// externals
import type { FocusEvent } from 'react';
import { useState, useEffect, useRef } from 'react';

// libraries
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { PriceInputProps } from '@interstate/components/PriceInput';
import { PriceInput as InterstatePriceInput } from '@interstate/components/PriceInput';
import { isBlurEvent } from '@makemydeal/dr-shared-ui-utils';
import { deferredSelect, useSelectOnFocusEnabledStrict } from '../selectOnFocus/selectOnFocus';
import type { SelectOnFocusSelector } from '../selectOnFocus/selectOnFocus';

type Props = Omit<PriceInputProps, 'value'> & { value: string; selectOnFocusSelector?: SelectOnFocusSelector };

export const PriceInput = (props: Props) => {
    const { value, onChange, onBlur, onKeyDown, onFocus, selectOnFocusSelector, ...rest } = props;
    const isAutoSelectEnabled = useSelectOnFocusEnabledStrict(selectOnFocusSelector);
    const [inputValue, setInputValue] = useState(value);
    const previousValueRef = useRef(value);
    const inputValueRef = useRef(value);

    useEffect(() => {
        inputValueRef.current = inputValue;
    }, [inputValue]);

    useEffect(() => {
        // Only sync if the prop value actually changed (not just formatting)
        if (value !== previousValueRef.current) {
            const numericValue = parseFloat(value);
            const numericInputValue = parseFloat(inputValueRef.current);
            if (numericValue !== numericInputValue) {
                setInputValue(value);
                inputValueRef.current = value;
            }
            previousValueRef.current = value;
        }
    }, [value]);

    return (
        <InterstatePriceInput
            {...rest}
            onFocus={(e: FocusEvent<HTMLInputElement>) => {
                onFocus?.(e);
                if (isAutoSelectEnabled) deferredSelect(e.currentTarget);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Tab') {
                    e.stopPropagation();
                }
                onKeyDown?.(e);
            }}
            onChange={(e: InterstateOnChangeEvent<string>) => {
                if (isBlurEvent(e)) {
                    return;
                }
                const newValue = e.target.value;
                setInputValue(newValue);
                inputValueRef.current = newValue;
                onChange?.(e);
            }}
            onBlur={(e: InterstateOnChangeEvent<string>) => {
                onBlur?.(e);
                if (e.target.value) {
                    const formattedValue = Number(e.target.value).toFixed(2);
                    setInputValue(formattedValue);
                    inputValueRef.current = formattedValue;
                } else {
                    setInputValue('');
                    inputValueRef.current = '';
                }
            }}
            value={inputValue}
            placeholder="0.00"
        />
    );
};

PriceInput.displayName = 'PriceInput';
